import * as LeakHelper from '@utils/leaks'
import { LodashUtils } from '@utils/lodash'

export namespace LeakManagement {
  export type LeakStatusType =
    | 'HighConsumption'
    | 'MajorLeak'
    | 'MinorLeak'
    | 'MasterMeter'
    | 'noLeak'

  export enum METER_ALERT_STATE {
    UNDEFINED,
    VALID,
    INVALID,
    REPORTED_VALID,
    REPORTED_INVALID,
  }

  export const VALID_STATES = [
    METER_ALERT_STATE.VALID,
    METER_ALERT_STATE.REPORTED_VALID,
  ]

  export const INVALID_STATES = [
    METER_ALERT_STATE.INVALID,
    METER_ALERT_STATE.REPORTED_INVALID,
  ]

  export const ALL_LEAK_STATES: METER_ALERT_STATE[] = Object.values(
    METER_ALERT_STATE,
  ).filter((val) => typeof val !== 'string') as METER_ALERT_STATE[]

  export const ALL_LEAK_STATUS: LeakStatusType[] = [
    'HighConsumption',
    'MasterMeter',
    'MajorLeak',
    'MinorLeak',
  ]

  export class MeterAlert {
    guid: string | null
    where: string | null
    comments: string | null
    cause: string | null
    state: METER_ALERT_STATE
    contractAccountNumber: string
    deviceNumber: string | null
    alarmCode: LeakStatusType
    alarmDescription: string | null
    extraInfo: string | null
    alarmStartDate: Date
    alarmEndDate: Date | null
    duration?: number
    meterSize: number | null
    leakVolume: number | null
    isLeakKnownBeforeNotification: boolean | null
    accountDisplayedName?: string

    constructor(initialValue: Partial<MeterAlert> | null) {
      this.guid = LodashUtils.get(initialValue, 'guid', null)
      this.where = LodashUtils.get(initialValue, 'where', null)
      this.comments = LodashUtils.get(initialValue, 'comments', null)
      this.cause = LodashUtils.get(initialValue, 'cause', null)
      this.state = LodashUtils.get(
        initialValue,
        'state',
        METER_ALERT_STATE.REPORTED_VALID,
      )
      this.contractAccountNumber = LodashUtils.get(
        initialValue,
        'contractAccountNumber',
        '',
      )
      this.deviceNumber = LodashUtils.get(initialValue, 'deviceNumber', null)
      this.alarmCode = LodashUtils.get(initialValue, 'alarmCode', 'noLeak')
      this.alarmDescription = LodashUtils.get(
        initialValue,
        'alarmDescription',
        null,
      )
      this.extraInfo = LodashUtils.get(initialValue, 'extraInfo', null)

      const startDate = LodashUtils.get(initialValue, 'alarmStartDate', null)
      const endDate = LodashUtils.get(initialValue, 'alarmEndDate', null)
      const validStartDate =
        Date.parse(startDate as any) > 0
          ? new Date(startDate as any)
          : new Date()
      const validEndDate =
        Date.parse(endDate as any) > 0 ? new Date(endDate as any) : null

      this.alarmStartDate = validStartDate
      this.alarmEndDate = validEndDate

      this.duration = LodashUtils.get(initialValue, 'duration', 0)
      this.meterSize = LodashUtils.get(initialValue, 'meterSize', null)
      this.leakVolume = LodashUtils.get(initialValue, 'leakVolume', null)
      this.isLeakKnownBeforeNotification = LodashUtils.get(
        initialValue,
        'isLeakKnownBeforeNotification',
        null,
      )

      this.accountDisplayedName = LodashUtils.get(
        initialValue,
        'accountDisplayedName',
        '',
      )
    }

    get isActualLeak() {
      if (LeakHelper.isLeakDetectedByUser(this)) {
        return this.state === METER_ALERT_STATE.REPORTED_VALID
      }
      return this.state === METER_ALERT_STATE.VALID
    }

    set isActualLeak(val: boolean) {
      if (LeakHelper.isLeakDetectedByUser(this)) {
        this.state = val
          ? METER_ALERT_STATE.REPORTED_VALID
          : METER_ALERT_STATE.REPORTED_INVALID
      } else {
        this.state = val ? METER_ALERT_STATE.VALID : METER_ALERT_STATE.INVALID
      }
      this.where = val ? this.where : null
    }

    get isResolved() {
      return LeakHelper.isValidDate(this.alarmEndDate || '')
    }

    set isResolved(val: boolean) {
      this.alarmEndDate = val ? new Date() : null
      this.comments = val ? this.comments : null
    }
  }

  export interface ICreateOrUpdateMeterAlertPayload {
    contractAccountNumber: string
    where?: string | null
    cause?: string | null
    comments?: string | null
    startDate?: Date | null
    endDate?: Date | null
    isLeakKnownBeforeNotification?: boolean | null
    state?: METER_ALERT_STATE | null
  }
}
